import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { updateAllGamesAverageRating } from '../lib/gameService';
import { getSystemStatistics, getRecentGameReviews, SystemStatistics } from '../lib/adminService';
import { createRecommendationRPC, verifyAndFixRecommendationsSchema } from '../lib/adminFunctions';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import SEO from '../components/SEO';
import { GameReview } from '../types';

// Componentes de UI Shadcn para o Modal
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"; // Para o botão de fechar no footer do dialog

// Componente de Ajuda
import GamificationConfigHelp from '../components/admin/gamification/GamificationConfigHelp';

// Importando os novos componentes
import DashboardContent from '../components/admin/DashboardContent';
import GamesContent from '../components/admin/GamesContent';
import SystemContent from '../components/admin/SystemContent';
import ImportContent from '../components/admin/ImportContent';
import NotificationsContent from '../components/admin/NotificationsContent';
import XpActionsAdmin from '../components/admin/XpActionsAdmin';
import BadgesAdminContent from '../components/admin/BadgesAdminContent';
import MissionsAdmin from '../components/admin/gamification/MissionsAdmin';
import SpecialEventsAdmin from '../components/admin/gamification/SpecialEventsAdmin';
import HeroBannersAdminContent from '../components/admin/HeroBannersAdminContent';
import YouTubeChannelsContent from '../components/admin/YouTubeChannelsContent';
import {
  getAllMascotsWithLevels,
  createMascot,
  updateMascot,
  deleteMascot,
  createMascotLevel,
  updateMascotLevel,
  deleteMascotLevel,
  Mascot,
  MascotLevel
} from '../lib/gamification/mascotService';
import { Edit2, Trash2, Plus } from 'lucide-react';

const AdminPage: React.FC = () => {
  const { user, isAdmin } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  
  // Estado para o modal de ajuda
  const [showConfigHelpModal, setShowConfigHelpModal] = useState(false);

  // Estados (mantidos aqui pois são usados por múltiplas abas ou pela lógica principal)
  const [loading, setLoading] = useState<boolean>(false); // Usado por GamesContent (update ratings)
  const [statsLoading, setStatsLoading] = useState<boolean>(true); // Usado por Dashboard, Games, System
  const [rpcLoading, setRpcLoading] = useState<boolean>(false); // Usado por SystemContent
  const [schemaFixLoading, setSchemaFixLoading] = useState(false); // Usado por SystemContent
  const [statistics, setStatistics] = useState<SystemStatistics | null>(null); // Usando SystemStatistics
  const [errorMessage, setErrorMessage] = useState<string | null>(null); // Mensagens gerais
  const [successMessage, setSuccessMessage] = useState<string | null>(null); // Mensagens gerais
  const [schemaFixMessage, setSchemaFixMessage] = useState<string | null>(null); // Usado por SystemContent
  const [activeTab, setActiveTab] = useState<string>('dashboard');
  const [recentLogins, setRecentLogins] = useState<any[]>([]); // Usado por Dashboard
  const [recentLoginsLoading, setRecentLoginsLoading] = useState<boolean>(true); // Usado por Dashboard
  const [systemHealth, setSystemHealth] = useState<{status: string, message: string}>({
    status: 'checking',
    message: 'Verificando status do sistema...'
  }); // Usado por Dashboard, System
  const [userRegistrations, setUserRegistrations] = useState<any[]>([]); // Usado por Dashboard
  const [registrationsLoading, setRegistrationsLoading] = useState<boolean>(true); // Usado por Dashboard
  const [loadingStores, setLoadingStores] = useState(false); // Usado por SystemContent
  const [stores, setStores] = useState<any[]>([]); // Usado por SystemContent
  const [storeError, setStoreError] = useState<string | null>(null); // Usado por SystemContent
  const [newStore, setNewStore] = useState({ name: '', url: '', logo_url: '', affiliate_code: '' }); // Usado por SystemContent
  const [editingStore, setEditingStore] = useState<any>(null); // Usado por SystemContent
  const [recentReviews, setRecentReviews] = useState<GameReview[]>([]); // Usado por Dashboard
  const [reviewsLoading, setReviewsLoading] = useState<boolean>(true); // Usado por Dashboard

  // Estados para Notificações
  const [notificationSegment, setNotificationSegment] = useState<string>('allUsers'); // Segmento padrão
  const [notificationParams, setNotificationParams] = useState<any>({}); // Params específicos do segmento (e.g., { gameId: '...' })
  const [notificationTitle, setNotificationTitle] = useState<string>('');
  const [notificationBody, setNotificationBody] = useState<string>('');
  const [sendingNotification, setSendingNotification] = useState<boolean>(false);
  
  // <<< Mapeamento de nomes de abas para exibição >>>
  const tabDisplayNames: { [key: string]: string } = {
    dashboard: 'Dashboard',
    games: 'Jogos',
    system: 'Sistema',
    importacao: 'Importação',
    notifications: 'Notificações',
    xp_actions: 'Ações de XP',
    badges: 'Badges',
    missions: 'Missões',
    special_events: 'Eventos Especiais',
    mascotes: 'Mascotes',
    hero_banners: 'Banners da Home',
    youtube_channels: 'Canais YouTube',
  };

  const [mascotes, setMascotes] = useState<Mascot[]>([]);
  const [loadingMascotes, setLoadingMascotes] = useState(false);
  const [showMascotModal, setShowMascotModal] = useState(false);
  const [editingMascot, setEditingMascot] = useState<Mascot | null>(null);
  const [mascotForm, setMascotForm] = useState<{ name: string; description: string; is_active: boolean; category?: string }>({ name: '', description: '', is_active: true });
  const [showLevelModal, setShowLevelModal] = useState(false);
  const [editingLevel, setEditingLevel] = useState<{ mascotId: string; level: MascotLevel | null }>({ mascotId: '', level: null });
  const [levelForm, setLevelForm] = useState<{ mascot_id: string; level_number: number; min_user_level: number; image_url: string }>({ mascot_id: '', level_number: 1, min_user_level: 1, image_url: '' });

  useEffect(() => {
    if (isAdmin === false) {
      navigate('/'); 
    } else if (isAdmin === true) { // Apenas carregar se admin for true
      loadDashboardData(); // Considerar renomear para loadAdminData ou carregar dados por aba
    }
  }, [isAdmin, navigate]); // Remover loadDashboardData daqui se for carregado por aba

  // Funções de carregamento de dados (mantidas aqui)
  const loadDashboardData = async () => {
    try {
      setStatsLoading(true);
      setRecentLoginsLoading(true);
      setRegistrationsLoading(true);
      setReviewsLoading(true);
      setLoadingStores(true); // Inicia loading de stores
      
      await Promise.allSettled([ // Use allSettled para que um erro não pare os outros
        loadStatistics(),
        loadRecentLogins(),
        checkSystemHealth(),
        loadUserRegistrations(),
        loadStores(),
        loadRecentReviews(),
        loadMascotes()
      ]);
    } catch (error) {
      console.error('Erro ao carregar dados do dashboard:', error);
      setErrorMessage("Erro ao carregar alguns dados do dashboard.");
    } finally {
       // Finaliza loadings individuais ou um geral
      setStatsLoading(false);
       setRecentLoginsLoading(false);
       setRegistrationsLoading(false);
       setReviewsLoading(false);
       setLoadingStores(false); 
    }
  };
  
  const loadRecentReviews = async () => {
    try {
      const reviews = await getRecentGameReviews(10); 
      setRecentReviews(reviews);
    } catch (error) {
      console.error('Erro ao carregar avaliações recentes:', error);
       toast.error("Erro ao carregar avaliações recentes."); 
    }
  };
  
  const loadStatistics = async () => {
    try {
      const stats = await getSystemStatistics();
      setStatistics(stats);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      toast.error("Erro ao carregar estatísticas.");
      setStatistics(null); // Define como null em caso de erro
    }
  };

  const loadRecentLogins = async () => {
    try {
      const { data, error } = await supabase
        .from('auth_events')
        .select('user_id, event_type, created_at, ip_address') 
        .order('created_at', { ascending: false })
        .limit(10);
      
      if (error) {
        if (error.code !== '42P01' && !error.message?.includes('not found')) {
        console.error('Erro ao carregar atividades recentes:', error);
            toast.error("Erro ao carregar atividades recentes.");
        }
        setRecentLogins([]);
        return;
      }
      setRecentLogins(data || []);
    } catch (error) {
      console.error('Erro ao carregar logins recentes:', error);
      toast.error("Erro crítico ao carregar logins recentes.");
      setRecentLogins([]);
    }
  };

  const checkSystemHealth = async () => {
     setSystemHealth({ status: 'checking', message: 'Verificando...' }); 
    try {
        const { error: simpleCheckError } = await supabase
            .from('games') 
            .select('id', { count: 'exact', head: true }); 

        if (simpleCheckError) {
             setSystemHealth({ status: 'error', message: 'Problemas na conexão com o banco de dados' });
          return;
      }
      
        // Se a função de verificação/correção existe, chama ela
        if (typeof verifyAndFixRecommendationsSchema === 'function') {
      const schemaOk = await verifyAndFixRecommendationsSchema();
      if (!schemaOk) {
                 setSystemHealth({ status: 'warning', message: 'Schema de recomendações inconsistente' });
                 return; // Sai se houver aviso no schema
             }
        }
        
        // Se chegou aqui, a conexão está OK e o schema (se verificado) está OK
        setSystemHealth({ status: 'ok', message: 'Todos os sistemas funcionando normalmente' });

    } catch (error) {
      console.error('Erro ao verificar saúde do sistema:', error);
        setSystemHealth({ status: 'error', message: 'Erro ao verificar status do sistema' });
    }
  };
  
  const loadUserRegistrations = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('created_at')
        .order('created_at', { ascending: true });
      
      if (error) {
        console.error('Erro ao carregar registros de usuários:', error);
        toast.error("Erro ao carregar registros de usuários.");
        setUserRegistrations([]);
        return;
      }
      setUserRegistrations(data || []);
    } catch (error) {
      console.error('Erro ao carregar registros de usuários:', error);
      toast.error("Erro crítico ao carregar registros.");
       setUserRegistrations([]);
    }
  };

  // Funções de ação (mantidas aqui, passadas como props)
  const handleUpdateAverageRatings = async () => {
      setLoading(true);
      setErrorMessage(null);
      setSuccessMessage(null);
    try {
      const result = await updateAllGamesAverageRating();
      if (result.success) {
        toast.success(`Avaliações atualizadas! ${result.updated} processados, ${result.failed} falhas.`);
        await loadStatistics(); // Recarrega as estatísticas
      } else {
        toast.error('Falha ao atualizar avaliações. Verifique o console.');
      }
    } catch (error: any) {
      console.error('Erro ao atualizar avaliações médias:', error);
      toast.error(`Erro ao atualizar avaliações: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateRecommendationRPC = async () => {
      setRpcLoading(true);
      setErrorMessage(null);
      setSuccessMessage(null);
    try {
      const result = await createRecommendationRPC();
      if (result) {
        toast.success('Função handle_recommendation criada/verificada!');
      } else {
        toast.error('Falha ao criar/verificar função handle_recommendation.');
      }
    } catch (error: any) {
      console.error('Erro ao criar função RPC:', error);
      toast.error(`Erro ao criar função RPC: ${error.message}`);
    } finally {
      setRpcLoading(false);
    }
  };
  
  const handleFixRecommendationSchema = async () => {
    setSchemaFixLoading(true);
    setSchemaFixMessage(null);
    try {
      const result = await verifyAndFixRecommendationsSchema();
      if (result) {
        setSchemaFixMessage('✅ Schema verificado e corrigido com sucesso.');
        toast.success('Schema verificado/corrigido.');
      } else {
        setSchemaFixMessage('⚠️ Não foi possível corrigir o schema ou já estava correto.');
        toast('Schema já estava correto ou não pôde ser corrigido.');
      }
       await checkSystemHealth(); // Re-verifica a saúde após a tentativa
    } catch (error: any) {
      console.error('Erro ao tentar corrigir schema:', error);
      setSchemaFixMessage(`❌ Erro ao tentar corrigir o schema: ${error.message}`);
      toast.error(`Erro ao corrigir schema: ${error.message}`);
    } finally {
      setSchemaFixLoading(false);
    }
  };
  
  // Funções de gerenciamento de lojas (mantidas aqui)
  const loadStores = async () => {
    setStoreError(null);
    try {
      const { data, error } = await supabase.from('stores').select('*').order('name');
      if (error) throw error;
      setStores(data || []);
    } catch (error: any) {
      console.error('Erro ao carregar lojas:', error);
      setStoreError(error.message || 'Erro ao carregar lojas');
       toast.error("Erro ao carregar lojas.");
    }
  };

  const addStore = async () => {
    if (!newStore.name || !newStore.url) {
      setStoreError('Nome e URL são obrigatórios');
      toast.error('Nome e URL da loja são obrigatórios.');
      return;
    }
    setLoadingStores(true);
    setStoreError(null);
    try {
      const { error } = await supabase.from('stores').insert([newStore]);
      if (error) throw error;
      setNewStore({ name: '', url: '', logo_url: '', affiliate_code: '' });
      await loadStores();
      toast.success('Loja adicionada com sucesso!');
    } catch (error: any) {
      console.error('Erro ao adicionar loja:', error);
      const errorMsg = error.message || 'Erro desconhecido';
      setStoreError(errorMsg);
      toast.error(`Erro ao adicionar loja: ${errorMsg}`);
    } finally {
      setLoadingStores(false);
    }
  };

  const updateStore = async () => {
    if (!editingStore || !editingStore.name || !editingStore.url) {
      setStoreError('Nome e URL são obrigatórios');
       toast.error('Nome e URL da loja são obrigatórios.');
      return;
    }
    setLoadingStores(true);
    setStoreError(null);
    try {
      const { error } = await supabase
        .from('stores')
        .update({ name: editingStore.name, url: editingStore.url, logo_url: editingStore.logo_url, affiliate_code: editingStore.affiliate_code })
        .eq('id', editingStore.id);
      if (error) throw error;
      setEditingStore(null);
      await loadStores();
      toast.success('Loja atualizada com sucesso!');
    } catch (error: any) {
      console.error('Erro ao atualizar loja:', error);
       const errorMsg = error.message || 'Erro desconhecido';
      setStoreError(errorMsg);
       toast.error(`Erro ao atualizar loja: ${errorMsg}`);
    } finally {
      setLoadingStores(false);
    }
  };

  const removeStore = async (id: string) => {
     toast((t) => (
        <div className="flex flex-col items-center">
          <p className="mb-2 text-center">Remover esta loja? <br/>(Produtos associados podem ser afetados)</p>
          <div className="flex gap-2">
             <button
              onClick={async () => {
                 toast.dismiss(t.id);
    setLoadingStores(true);
    setStoreError(null);
                 try {
                    const { error } = await supabase.from('stores').delete().eq('id', id);
                    if (error) throw error;
      await loadStores();
                    toast.success('Loja removida!', { id: 'store-remove-success' });
    } catch (error: any) {
      console.error('Erro ao remover loja:', error);
                    const errorMsg = error.message || 'Erro desconhecido';
                    setStoreError(errorMsg);
                    toast.error(`Erro ao remover: ${errorMsg}`, { id: 'store-remove-error' });
    } finally {
      setLoadingStores(false);
    }
              }}
              className="px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700 text-sm font-medium"
            >
              Remover
            </button>
            <button
              onClick={() => toast.dismiss(t.id)}
              className="px-3 py-1 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 text-sm font-medium"
            >
              Cancelar
            </button>
          </div>
        </div>
      ), { duration: Infinity }); 
  };
  
  // Função para enviar notificações
  const handleSendNotification = async () => {
    if (!notificationSegment || !notificationTitle || !notificationBody) {
      toast.error("Segmento, Título e Corpo da notificação são obrigatórios.");
      return;
    }

    // Validar parâmetros específicos do segmento (wishlistGame: ID ou Nome)
    if (notificationSegment === 'wishlistGame' && !notificationParams.gameId && !notificationParams.gameName) {
        toast.error("O ID ou Nome do Jogo é obrigatório para o segmento 'Wishlist Game'.");
        return;
    }
     // Adicionar validações para outros segmentos aqui...

    setSendingNotification(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    // Monta o payload garantindo que params contenham gameId ou gameName (ou ambos se digitados)
    const bodyPayload = {
      segmentType: notificationSegment,
      params: {
        // Inclui todos os params existentes (pode incluir gameId e gameName)
        ...notificationParams,
        messageTitle: notificationTitle,
        messageBody: notificationBody,
      }
    };

    // Remover gameId se gameName estiver presente (para evitar ambiguidade, backend prioriza gameId se ambos existirem)
    // Ou manter ambos e deixar o backend decidir (como está agora)
    // if (bodyPayload.params.gameName && bodyPayload.params.gameId) {
    //    delete bodyPayload.params.gameId; 
    // }

    try {
      console.log('Invocando função send-segmented-notification com:', bodyPayload);
      const { data, error } = await supabase.functions.invoke('send-segmented-notification', {
        body: bodyPayload, 
      });

      if (error) {
        throw error; // Lança o erro para o catch
      }

      // Verifica a resposta da função (que definimos no backend)
      if (data && data.success) {
        const successMsg = `Notificações enviadas! Sucesso: ${data.notificationsSent}, Falhas: ${data.failures}.`;
        toast.success(successMsg);
        setSuccessMessage(successMsg);
        // Limpar formulário opcionalmente
        // setNotificationParams({});
        // setNotificationTitle('');
        // setNotificationBody('');
      } else {
         // Se data.success for false ou data for inesperado
         const errorMsg = data?.error || data?.message || 'Resposta inesperada da função.';
         throw new Error(errorMsg);
      }

    } catch (error: any) {
      console.error('Erro ao enviar notificação:', error);
      const errorDetail = error.message || (typeof error === 'string' ? error : 'Erro desconhecido');
      toast.error(`Falha ao enviar: ${errorDetail}`);
      setErrorMessage(`Falha ao enviar: ${errorDetail}`);
    } finally {
      setSendingNotification(false);
    }
  };
  
  // Função para renderizar o conteúdo da aba ativa usando os componentes importados
  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return <DashboardContent 
                  statsLoading={statsLoading} 
                  statistics={statistics} 
                  registrationsLoading={registrationsLoading} 
                  userRegistrations={userRegistrations} 
                  systemHealth={systemHealth} 
                  checkSystemHealth={checkSystemHealth}
                  reviewsLoading={reviewsLoading}
                  recentReviews={recentReviews}
                  recentLoginsLoading={recentLoginsLoading}
                  recentLogins={recentLogins}
                />;
      case 'games':
        return <GamesContent 
                  loading={loading}
                  statsLoading={statsLoading}
                  statistics={statistics}
                  handleUpdateAverageRatings={handleUpdateAverageRatings}
                />;
      case 'system':
        return <SystemContent
                  rpcLoading={rpcLoading}
                  handleCreateRecommendationRPC={handleCreateRecommendationRPC}
                  schemaFixLoading={schemaFixLoading}
                  handleFixRecommendationSchema={handleFixRecommendationSchema}
                  schemaFixMessage={schemaFixMessage}
                  systemHealth={systemHealth}
                  statsLoading={statsLoading}
                  statistics={statistics}
                  loadingStores={loadingStores}
                  stores={stores}
                  storeError={storeError}
                  editingStore={editingStore}
                  setEditingStore={setEditingStore}
                  updateStore={updateStore}
                  removeStore={removeStore}
                  newStore={newStore}
                  setNewStore={setNewStore}
                  addStore={addStore}
                />;
      case 'importacao':
        return <ImportContent />;
      case 'notifications':
        return <NotificationsContent
                  segment={notificationSegment}
                  setSegment={setNotificationSegment}
                  params={notificationParams}
                  setParams={setNotificationParams}
                  title={notificationTitle}
                  setTitle={setNotificationTitle}
                  body={notificationBody}
                  setBody={setNotificationBody}
                  isLoading={sendingNotification}
                  onSubmit={handleSendNotification}
                />;
      case 'xp_actions':
        return <XpActionsAdmin />;
      case 'badges':
        return <BadgesAdminContent />;
      case 'missions':
        return <MissionsAdmin onOpenHelp={() => setShowConfigHelpModal(true)} />;
      case 'special_events':
        return <SpecialEventsAdmin onOpenHelp={() => setShowConfigHelpModal(true)} />;
      case 'mascotes':
        return renderMascotesTab();
      case 'hero_banners':
        return <HeroBannersAdminContent />;
      case 'youtube_channels':
        return <YouTubeChannelsContent />;
      default:
        return null;
    }
  };

  // Carregar mascotes
  const loadMascotes = async () => {
    setLoadingMascotes(true);
    const mascots = await getAllMascotsWithLevels(supabase);
    setMascotes(mascots);
    setLoadingMascotes(false);
  };

  useEffect(() => {
    if (activeTab === 'mascotes') loadMascotes();
  }, [activeTab]);

  // CRUD Mascote
  const handleMascotSave = async () => {
    if (editingMascot) {
      await updateMascot(supabase, editingMascot.id, { ...mascotForm, category: mascotForm.category || null });
    } else {
      await createMascot(supabase, { ...mascotForm, category: mascotForm.category || null });
    }
    setShowMascotModal(false);
    setEditingMascot(null);
    setMascotForm({ name: '', description: '', is_active: true });
    loadMascotes();
  };
  const handleMascotEdit = (mascot: Mascot) => {
    setEditingMascot(mascot);
    setMascotForm({ name: mascot.name, description: mascot.description || '', is_active: mascot.is_active, category: mascot.category || undefined });
    setShowMascotModal(true);
  };
  const handleMascotDelete = async (mascotId: string) => {
    if (window.confirm('Tem certeza que deseja deletar este mascote?')) {
      await deleteMascot(supabase, mascotId);
      loadMascotes();
    }
  };

  // CRUD Nível
  const handleLevelSave = async () => {
    if (editingLevel.level) {
      await updateMascotLevel(supabase, editingLevel.level.id, levelForm);
    } else {
      await createMascotLevel(supabase, { ...levelForm, mascot_id: editingLevel.mascotId });
    }
    setShowLevelModal(false);
    setEditingLevel({ mascotId: '', level: null });
    setLevelForm({ mascot_id: '', level_number: 1, min_user_level: 1, image_url: '' });
    loadMascotes();
  };
  const handleLevelEdit = (mascotId: string, level: MascotLevel) => {
    setEditingLevel({ mascotId, level });
    setLevelForm({ mascot_id: mascotId, level_number: level.level_number, min_user_level: level.min_user_level, image_url: level.image_url });
    setShowLevelModal(true);
  };
  const handleLevelDelete = async (levelId: string) => {
    if (window.confirm('Tem certeza que deseja deletar este nível?')) {
      await deleteMascotLevel(supabase, levelId);
      loadMascotes();
    }
  };
  const handleLevelAdd = (mascotId: string) => {
    setEditingLevel({ mascotId, level: null });
    setLevelForm({ mascot_id: mascotId, level_number: 1, min_user_level: 1, image_url: '' });
    setShowLevelModal(true);
  };

  // Renderização da aba Mascotes
  const renderMascotesTab = () => (
    <div>
      <h2 className={`text-2xl font-bold mb-2 ${theme === 'dark' ? 'text-gray-100' : 'text-gray-800'}`}>Mascotes</h2>
      <p className={`mb-4 max-w-2xl ${theme === 'dark' ? 'text-gray-400' : 'text-gray-700'}`}>
        Mascotes são avatares colecionáveis que acompanham o usuário e evoluem conforme o progresso. Cada mascote pode ter vários níveis, desbloqueados conforme o nível do usuário. Use este painel para gerenciar os mascotes disponíveis na plataforma.
      </p>
      <button className={`mb-6 px-4 py-2 text-white rounded shadow transition flex items-center gap-2 ${theme === 'dark' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'}`} onClick={() => { setEditingMascot(null); setMascotForm({ name: '', description: '', is_active: true }); setShowMascotModal(true); }}>
        <Plus className="w-4 h-4" /> Novo Mascote
      </button>
      {loadingMascotes ? <div className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>Carregando...</div> : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {mascotes.map(mascot => (
            <div key={mascot.id} className={`border rounded-lg p-5 shadow-sm ${theme === 'dark' ? 'bg-gray-800 border-gray-700' : 'bg-white'}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <span className={`font-semibold text-lg ${theme === 'dark' ? 'text-gray-100' : 'text-gray-900'}`}>{mascot.name}</span> {mascot.is_active ? <span className="text-green-600 dark:text-green-400 text-xs ml-2">(Ativo)</span> : <span className="text-gray-400 dark:text-gray-500 text-xs ml-2">(Inativo)</span>}
                  <div className={`text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>{mascot.description}</div>
                </div>
                <div className="flex gap-2">
                  <button title="Editar mascote" className={`px-2 py-1 rounded flex items-center gap-1 ${theme === 'dark' ? 'bg-yellow-500 hover:bg-yellow-600 text-gray-900' : 'bg-yellow-400 hover:bg-yellow-500'}`} onClick={() => handleMascotEdit(mascot)}>
                    <Edit2 className="w-4 h-4" /> Editar
                  </button>
                  <button title="Deletar mascote" className={`px-2 py-1 text-white rounded flex items-center gap-1 ${theme === 'dark' ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'}`} onClick={() => handleMascotDelete(mascot.id)}>
                    <Trash2 className="w-4 h-4" /> Deletar
                  </button>
                </div>
              </div>
              <div className="mt-4">
                <div className="flex items-center justify-between mb-1">
                  <span className={`font-semibold ${theme === 'dark' ? 'text-gray-200' : 'text-gray-800'}`}>Níveis do mascote</span>
                  <button className={`px-2 py-1 text-white rounded flex items-center gap-1 ${theme === 'dark' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-blue-400 hover:bg-blue-500'}`} onClick={() => handleLevelAdd(mascot.id)}>
                    <Plus className="w-4 h-4" /> Adicionar Nível
                  </button>
                </div>
                <ul className="mt-2 space-y-2">
                  {mascot.levels.length === 0 && <li className={`text-sm ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>Nenhum nível cadastrado.</li>}
                  {mascot.levels.map(level => (
                    <li key={level.id} className={`flex items-center justify-between border-b py-1 ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                      <span className={`${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                        <span className="font-semibold">Nível {level.level_number}</span> (mín. nível usuário: {level.min_user_level})
                        {level.image_url && <img src={level.image_url} alt="img" className="inline h-6 ml-2 align-middle rounded" />}
                      </span>
                      <div className="flex gap-2">
                        <button title="Editar nível" className={`px-2 py-1 rounded flex items-center ${theme === 'dark' ? 'bg-yellow-500 hover:bg-yellow-600 text-gray-900' : 'bg-yellow-400 hover:bg-yellow-500'}`} onClick={() => handleLevelEdit(mascot.id, level)}>
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button title="Deletar nível" className={`px-2 py-1 text-white rounded flex items-center ${theme === 'dark' ? 'bg-red-600 hover:bg-red-700' : 'bg-red-500 hover:bg-red-600'}`} onClick={() => handleLevelDelete(level.id)}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      )}
      {/* Modal Mascote */}
      {showMascotModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`p-6 rounded shadow-lg w-96 ${theme === 'dark' ? 'bg-gray-800 text-gray-200' : 'bg-white'}`}>
            <h3 className="font-bold mb-2 text-lg">{editingMascot ? 'Editar Mascote' : 'Novo Mascote'}</h3>
            <p className={`text-xs mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Preencha os dados do mascote. O nome será exibido para os usuários. A descrição pode explicar a personalidade ou função do mascote.</p>
            <label className="block mb-1 text-sm font-medium">Nome do mascote *</label>
            <input className={`w-full mb-2 border p-1 rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'border-gray-300'}`} placeholder="Ex: Meeple, Dragão, Robô..." value={mascotForm.name} onChange={e => setMascotForm(f => ({ ...f, name: e.target.value }))} />
            <label className="block mb-1 text-sm font-medium">Descrição</label>
            <textarea className={`w-full mb-2 border p-1 rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'border-gray-300'}`} placeholder="Ex: Um mascote divertido que incentiva o usuário a jogar mais." value={mascotForm.description} onChange={e => setMascotForm(f => ({ ...f, description: e.target.value }))} />
            <label className="flex items-center mb-2 text-sm">
              <input type="checkbox" checked={mascotForm.is_active} onChange={e => setMascotForm(f => ({ ...f, is_active: e.target.checked }))} className="mr-2 form-checkbox dark:bg-gray-700 dark:border-gray-600" /> Ativo (visível para escolha)
            </label>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Categoria do mascote <span className={`${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>(ex: "Animal", "Robô", "Fantasia")</span></label>
              <input
                type="text"
                className={`w-full border rounded px-3 py-2 ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'border-gray-300'}`}
                value={mascotForm.category || ''}
                onChange={e => setMascotForm({ ...mascotForm, category: e.target.value })}
                placeholder="Digite a categoria do mascote"
              />
            </div>
            <div className="flex justify-end mt-4 gap-2">
              <button className={`px-3 py-1 rounded ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-300 hover:bg-gray-400'}`} onClick={() => setShowMascotModal(false)}>Cancelar</button>
              <button className={`px-3 py-1 text-white rounded ${theme === 'dark' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'}`} onClick={handleMascotSave}>{editingMascot ? 'Salvar' : 'Criar'}</button>
            </div>
          </div>
        </div>
      )}
      {/* Modal Nível */}
      {showLevelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`p-6 rounded shadow-lg w-96 ${theme === 'dark' ? 'bg-gray-800 text-gray-200' : 'bg-white'}`}>
            <h3 className="font-bold mb-2 text-lg">{editingLevel.level ? 'Editar Nível do Mascote' : 'Novo Nível do Mascote'}</h3>
            <p className={`text-xs mb-3 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Cada nível representa uma evolução visual do mascote, desbloqueada conforme o nível do usuário.</p>
            <label className="block mb-1 text-sm font-medium">Número do nível *</label>
            <input className={`w-full mb-2 border p-1 rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'border-gray-300'}`} placeholder="Ex: 1, 2, 3..." type="number" value={levelForm.level_number} onChange={e => setLevelForm(f => ({ ...f, level_number: Number(e.target.value) }))} />
            <label className="block mb-1 text-sm font-medium">Nível mínimo do usuário *</label>
            <input className={`w-full mb-2 border p-1 rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'border-gray-300'}`} placeholder="Ex: 1, 5, 10..." type="number" value={levelForm.min_user_level} onChange={e => setLevelForm(f => ({ ...f, min_user_level: Number(e.target.value) }))} />
            <label className="block mb-1 text-sm font-medium">URL da imagem *</label>
            <input className={`w-full mb-2 border p-1 rounded ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400' : 'border-gray-300'}`} placeholder="Link para a imagem do mascote neste nível" value={levelForm.image_url} onChange={e => setLevelForm(f => ({ ...f, image_url: e.target.value }))} />
            <span className={`text-xs mb-2 block ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>Dica: Use imagens quadradas e pequenas para melhor visualização.</span>
            <div className="flex justify-end mt-4 gap-2">
              <button className={`px-3 py-1 rounded ${theme === 'dark' ? 'bg-gray-600 hover:bg-gray-500 text-gray-200' : 'bg-gray-300 hover:bg-gray-400'}`} onClick={() => setShowLevelModal(false)}>Cancelar</button>
              <button className={`px-3 py-1 text-white rounded ${theme === 'dark' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-blue-600 hover:bg-blue-700'}`} onClick={handleLevelSave}>{editingLevel.level ? 'Salvar' : 'Criar'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Proteção inicial - renderiza null ou um loader se ainda não verificado
  if (isAdmin === null) { // Verifica se isAdmin ainda não foi determinado
     return (
        <div className={`flex justify-center items-center h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gray-100'}`}>
           {/* Pode adicionar um loader aqui */}
        </div>
     );
  }
  
  // Redirecionamento se não for admin
  if (!isAdmin) {
    return (
      <div className={`container mx-auto px-4 py-8 ${theme === 'dark' ? 'bg-gray-900 text-gray-300' : 'bg-gray-100 text-gray-800'}`}>
        <div className={`${theme === 'dark' ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800'} p-4 rounded-md`}>
           <p>Acesso negado. Redirecionando...</p>
        </div>
      </div>
    );
  }
  
  // Renderização principal do AdminPage
  return (
    <>
      <SEO 
        title="Painel Administrativo"
        description="Painel de gerenciamento administrativo do Boardverso."
      />
      <div className={`container mx-auto px-4 py-8 max-w-7xl ${theme === 'dark' ? 'text-gray-300' : ''}`}>
        <div className="flex flex-col md:flex-row justify-between items-center mb-6 gap-4">
          <h1 className={`text-2xl font-bold ${theme === 'dark' ? 'text-gray-50' : 'text-gray-800'}`}>Painel Administrativo</h1>
          
          <div className={`flex flex-wrap justify-center space-x-1 p-1 rounded-lg shadow-sm ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-100'}`}>
            {Object.keys(tabDisplayNames).map(tabKey => (
            <button 
                key={tabKey}
                onClick={() => setActiveTab(tabKey)}
                className={`px-4 py-2 text-sm font-medium rounded-md capitalize transition-colors duration-150 ${ 
                  activeTab === tabKey 
                    ? (theme === 'dark' ? 'bg-gray-600 shadow text-indigo-300 ring-1 ring-indigo-500' : 'bg-white shadow text-indigo-700 ring-1 ring-indigo-200') 
                    : (theme === 'dark' ? 'text-gray-400 hover:text-indigo-300 hover:bg-gray-600' : 'text-gray-600 hover:text-indigo-700 hover:bg-gray-200')
                }`}
              >
                {tabDisplayNames[tabKey]}
            </button>
            ))}
              </div>
            </div>
            
        <div className="mt-6">
          {renderActiveTabContent()} 
        </div>

        {/* Modal de Ajuda para Configuração de Gamificação */}
        <Dialog open={showConfigHelpModal} onOpenChange={setShowConfigHelpModal}>
          <DialogContent className={`max-w-3xl max-h-[90vh] ${theme === 'dark' ? 'dark bg-gray-800 border-gray-700' : 'bg-white'}`}>
            <DialogHeader>
              <DialogTitle className={`${theme === 'dark' ? 'text-gray-50' : 'text-gray-900'}`}>Referência de Configuração JSON para Gamificação</DialogTitle>
              <DialogDescription className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                Guia para configurar os campos JSON de Missões, Badges e Eventos Especiais.
              </DialogDescription>
            </DialogHeader>
            <div className="overflow-y-auto pr-6">
              <GamificationConfigHelp />
            </div>
            <DialogFooter className="sm:justify-start">
              <DialogClose asChild>
                <Button type="button" variant="secondary">
                  Fechar
                </Button>
              </DialogClose>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
};

export default AdminPage;